// Page view for the chart-surface sheets — the Guitar Pro model: the sheet as
// side-by-side pages that fit the band between the sticky header and the
// transport bar, instead of one vertical strip. On a wide desktop a 32-bar
// tune reads whole, like a book open on a stand.
//
// The split of labour: `sheetPages.ts` decides (pure, tested), this component
// MEASURES — the available band, and the sheet's real row stride — because row
// heights are CSS's business (diagram caps, ending-row lanes, the chord-scale
// variables) and guessing them in JS is how layouts drift.
//
// Measurement rules, each load-bearing:
//  - The band is rect-based (this element's document top → the fixed transport
//    bar's top), so focus mode's collapsed chrome grows the pages for free and
//    the current scroll position never skews the numbers (document offsets and
//    fixed elements are both scroll-invariant).
//  - The row stride is the distance between the FIRST TWO row tops on page 1.
//    Page 1 always starts at row 0, so this pair never changes as pagination
//    reflows — an average over the page's rows would feed back into
//    rows-per-page and oscillate when the band sits near a row boundary.
//  - Every measurement passes the MEASURE GATE (sheetPages.ts) before it may
//    become state: an unchanged one schedules nothing (relying on the
//    updater to bail is not enough — during nested sync commits React
//    schedules each call before bailing and unmounts the tree at 50), and a
//    genuinely ALTERNATING one is a layout-feedback cycle (masthead wrap ↔
//    spread width, scrollbar ↔ page count) and is held. Both were user-found
//    as "blank screen": once on resize, once on a cold tune load.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { useStore } from '@/state/store';
import { scaleFactor } from '@/state/displayScale';
import type { IRealChart } from '@/music/ireal/types';
import {
  chartCellsForPaper,
  chartCellsForWidth,
  chartRowCount,
  computeLayout,
  diagramBarWidth,
  diagramSlotChords,
  halfWidthBreakpoint,
  useChartCells,
} from './chartLayout';
import { PRINT_CONTENT_WIDTH, PrintSheet } from './PrintSheet';
import { usePrintMode } from './printMode';
import { measureSheetBand, MIN_SHEET_PAGE_HEIGHT } from './sheetBand';
import { ReadingChartProbe } from './ReadingChartProbe';
import { readingChartVars, useReadingChartMetrics } from './useReadingChartMetrics';
import type { ReadingChartLayout } from './readingChartLayout';
import './readingChart.css';
import {
  admitMeasure,
  createMeasureGate,
  MAX_SHEET_PAGE_WIDTH,
  MEASURE_SETTLE_MS,
  measureSignature,
  MIN_SHEET_PAGE_WIDTH,
  planSheetPages,
  SHEET_PAGE_GAP,
  sheetPageWidth,
} from './sheetPages';

/** Below the grid: its own bottom margin plus the page-number lane. */
const PAGE_BOTTOM_CHROME = 40;

/** The page-card row itself — shared presentation for every pager (the chart
 * sheets here, the score surface's pager in NotationPane), so the paper look,
 * testids and page numbers cannot drift apart. */
export function SheetPageCards({
  gridRef,
  columns,
  pageWidth,
  minHeight,
  windows,
  render,
}: {
  gridRef?: Ref<HTMLDivElement>;
  columns: number;
  pageWidth: number;
  minHeight: number;
  windows: readonly (readonly [number, number])[];
  render: (window: readonly [number, number], index: number) => ReactNode;
}) {
  return (
    <div
      ref={gridRef}
      className="sheet-pages-grid"
      data-testid="sheet-pages"
      style={{
        gridTemplateColumns: `repeat(${columns}, ${pageWidth}px)`,
        gap: SHEET_PAGE_GAP,
      }}
    >
      {windows.map((win, i) => (
        <section
          key={i}
          className="sheet-page"
          aria-label={`Page ${i + 1} of ${windows.length}`}
          style={{ minHeight }}
        >
          {render(win, i)}
          {windows.length > 1 && (
            <span className="sheet-page-num" aria-hidden="true">
              {i + 1} / {windows.length}
            </span>
          )}
        </section>
      ))}
    </div>
  );
}

interface PagedSheetProps {
  /** Chord-only reading uses measured symbols, not diagram breakpoints. */
  reading?: boolean;
  chart: IRealChart;
  /** Off = one flowing sheet; on = measured columns of complete rows. */
  active: boolean;
  /** Arrange diagrams use the score scale on both surfaces. */
  scale?: number;
  /** The masthead (ChartHeader). A plain sibling when inactive — the
   * `.chart-header + .chart-view` adjacency rule depends on that — and in
   * page view it prints ON page 1's paper, as an engraved score does: it is
   * then the width of its page (never the monitor), and only page 1 pays for
   * it, later pages giving the band back to the music. */
  header?: ReactNode;
  /** Renders one sheet: the whole thing (window undefined) or one page's rows. */
  render: (
    rowWindow: readonly [number, number] | undefined,
    cellsPerRow: number,
    /** Grips per bar slot at the width drawn (diagram sheets only). */
    slotChords?: number,
    readingLayout?: ReadingChartLayout,
  ) => ReactNode;
}

interface Measured {
  availWidth: number;
  availHeight: number;
  rowStride: number;
  rowGap: number;
  /** Page 1's content inset: card padding PLUS the masthead that sits on it. */
  topChrome: number;
  /** What the masthead itself occupies (its height + the gap to the music),
   * so pages 2+ can hand that space back to the music. */
  headerBlock: number;
}

export function PagedSheet({
  chart,
  active,
  header,
  render,
  scale,
  reading = false,
}: PagedSheetProps) {
  const viewportCells = useChartCells(chart, scale);
  // While printing, the flowing sheet is hidden (@media print) and the print
  // sheet below is what draws — so the pager stops measuring a box that has
  // no layout, and picks its band up again when the paper is gone.
  const printing = usePrintMode();
  const paging = active && !printing;
  // Only subscribed so chrome collapsing/expanding re-measures the band.
  const focusMode = useStore((s) => s.focusMode);
  const savedChartScale = useStore((s) => s.chartScale);
  const chartScale = scale ?? savedChartScale;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const readingRef = useRef<HTMLDivElement>(null);
  const [stripWidth, setStripWidth] = useState(700);
  useLayoutEffect(() => {
    if (!reading || !readingRef.current) return;
    const el = readingRef.current;
    const measureWidth = () => {
      const width = Math.round(el.getBoundingClientRect().width);
      if (width > 0) setStripWidth((old) => (old === width ? old : width));
    };
    measureWidth();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureWidth);
    observer?.observe(el);
    window.addEventListener('resize', measureWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measureWidth);
    };
  }, [reading]);
  const [m, setM] = useState<Measured>(() => ({
    availWidth: typeof window !== 'undefined' ? Math.max(320, window.innerWidth - 48) : 1200,
    availHeight:
      typeof window !== 'undefined'
        ? Math.max(MIN_SHEET_PAGE_HEIGHT, window.innerHeight - 260)
        : 640,
    rowStride: 0,
    rowGap: 0,
    topChrome: 30,
    headerBlock: 0,
  }));
  // Decides OUTSIDE React state whether a measurement may become state:
  // an unchanged one schedules nothing, and a genuinely ALTERNATING one (a
  // layout-feedback cycle — see the gate's doc in sheetPages.ts) is held, or
  // React's nested-update counter unmounts the tree at 50.
  const gateRef = useRef(createMeasureGate());
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A page must clear the chart's legibility breakpoint, so it always carries
  // the authored 4-bar grid: raising Chord size widens the pages (and fits
  // fewer per row) instead of silently halving the grid — the Guitar Pro zoom
  // trade. Clamped to the classic canvas cap; a chart whose breakpoint
  // exceeds even that reflows honestly at the cap.
  const minPageWidth = useMemo(
    () =>
      Math.min(
        MAX_SHEET_PAGE_WIDTH,
        Math.max(
          MIN_SHEET_PAGE_WIDTH,
          reading ? MIN_SHEET_PAGE_WIDTH : halfWidthBreakpoint(chart, scaleFactor(chartScale)) + 1,
        ),
      ),
    [chart, chartScale, reading],
  );
  const { pageWidth, fit: pageFits } = useMemo(
    () => sheetPageWidth(m.availWidth, minPageWidth),
    [m.availWidth, minPageWidth],
  );
  const minBarWidth = scale === undefined ? undefined : diagramBarWidth(scaleFactor(chartScale));
  const cells = useMemo(
    () => chartCellsForWidth(chart, scaleFactor(chartScale), pageWidth, minBarWidth),
    [chart, chartScale, pageWidth, minBarWidth],
  );
  const slotChords = useMemo(
    () =>
      scale === undefined
        ? undefined
        : diagramSlotChords(pageWidth, cells, scaleFactor(chartScale)),
    [scale, pageWidth, cells, chartScale],
  );
  const readingWidth = printing
    ? PRINT_CONTENT_WIDTH
    : active
      ? pageWidth - (pageFits > 1 ? 25 : 8)
      : stripWidth;
  const { ref: probeRef, layout: readingLayout } = useReadingChartMetrics(
    chart,
    readingWidth,
    scaleFactor(chartScale),
    reading,
  );
  const readingStyle = readingChartVars(chart, readingWidth, scaleFactor(chartScale));
  const totalRows = useMemo(() => {
    if (reading) return readingLayout.rowCount;
    const layout = computeLayout(chart, cells, slotChords);
    return layout.length ? layout[layout.length - 1].row + 1 : 0;
  }, [chart, cells, slotChords, reading, readingLayout]);
  const plan = useMemo(
    () =>
      planSheetPages({
        availWidth: m.availWidth,
        minPageWidth,
        // Page 1 carries the masthead, so it holds fewer rows; every later
        // page gets that band back (the printed-score convention).
        firstPageContentHeight: Math.max(0, m.availHeight - m.topChrome - PAGE_BOTTOM_CHROME),
        contentHeight: Math.max(
          0,
          m.availHeight - (m.topChrome - m.headerBlock) - PAGE_BOTTOM_CHROME,
        ),
        totalRows,
        rowStride: m.rowStride,
        rowGap: m.rowGap,
      }),
    [m, minPageWidth, totalRows],
  );
  const measure = useCallback(() => {
    const el = containerRef.current;
    const pagesEl = gridRef.current;
    if (!el || !pagesEl) return;
    const { availWidth, availHeight } = measureSheetBand(el, pagesEl);

    let rowStride = 0;
    let rowGap = 0;
    let topChrome = 30;
    let headerBlock = 0;
    // Page 1's own grid (document order), which is where the masthead sits.
    const grid = el.querySelector<HTMLElement>(
      '.sheet-page .chart-view, .sheet-page .optimized-chart-grid',
    );
    if (grid) {
      rowGap = Number.parseFloat(getComputedStyle(grid).rowGap) || 0;
      const tops = Array.from(
        grid.querySelectorAll<HTMLElement>('[data-measure-id]'),
        (bar) => bar.getBoundingClientRect().top,
      ).sort((a, b) => a - b);
      const distinct: number[] = [];
      for (const t of tops) {
        if (distinct.length === 0 || t - distinct[distinct.length - 1] > 6) distinct.push(t);
      }
      rowStride =
        distinct.length >= 2
          ? distinct[1] - distinct[0]
          : grid.getBoundingClientRect().height + rowGap;
      const page = grid.closest('.sheet-page');
      if (page) {
        const gridTop = grid.getBoundingClientRect().top;
        topChrome = Math.max(0, gridTop - page.getBoundingClientRect().top);
        // The masthead's band = its top to the music's top, so it carries its
        // own margins without this needing to know what they are.
        const headerEl = page.querySelector<HTMLElement>('.sheet-page-header');
        if (headerEl) {
          headerBlock = Math.max(0, gridTop - headerEl.getBoundingClientRect().top);
        }
      }
    }

    const signature = measureSignature([
      availWidth,
      availHeight,
      rowStride,
      rowGap,
      topChrome,
      headerBlock,
    ]);
    const verdict = admitMeasure(gateRef.current, signature, performance.now());
    // A refusal is not the end of the story. `busy` means the burst was full —
    // ordinary during a drag, whose LAST event lands in a full burst — so
    // re-measure once the burst expires, or the layout stays frozen at a
    // mid-drag size (pagination stuck, or gone when the held width sits under
    // the page minimum). `cycling` is deliberately NOT retried: that hold is
    // the point, and retrying would turn it into a slow flip-flop.
    if (verdict !== 'admitted') {
      if (verdict === 'busy' && retryRef.current === null) {
        retryRef.current = setTimeout(() => {
          retryRef.current = null;
          measureRef.current();
        }, MEASURE_SETTLE_MS + 60);
      }
      return;
    }
    setM({ availWidth, availHeight, rowStride, rowGap, topChrome, headerBlock });
  }, []);
  const measureRef = useRef(measure);
  measureRef.current = measure;
  useEffect(
    () => () => {
      if (retryRef.current !== null) clearTimeout(retryRef.current);
    },
    [],
  );

  // After every render while active: converges via the ≥1px change guard.
  useLayoutEffect(() => {
    if (paging) measure();
  });
  useEffect(() => {
    if (!paging) return;
    const el = containerRef.current;
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (el && observer) observer.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [paging, measure]);
  // Focus mode animates the chrome away; measure again once it has settled.
  useEffect(() => {
    if (!paging) return;
    const timer = setTimeout(measure, 350);
    return () => clearTimeout(timer);
  }, [paging, focusMode, measure]);

  // One block per LINE of the sheet, so the printer may break between them.
  // Bars per line come from the PAPER's width, not the window's: the same
  // legibility rule the screen uses, asked about the page it will actually
  // print on. Four bars to a line is the printed-chart convention and what an
  // ordinary chart takes; a densely authored one (three chords in a bar,
  // a crowded turnaround) drops to two, because at 673px four would print its
  // symbols on top of each other — measured on Body And Soul.
  const printCells = chartCellsForPaper(chart, scaleFactor(chartScale), PRINT_CONTENT_WIDTH);
  const printSlots =
    scale === undefined
      ? undefined
      : diagramSlotChords(PRINT_CONTENT_WIDTH, printCells, scaleFactor(chartScale));
  const printSheet = printing ? (
    <PrintSheet
      header={header}
      count={reading ? readingLayout.rowCount : chartRowCount(chart, printCells, printSlots)}
      render={(rowWindow) =>
        render(rowWindow, printCells, printSlots, reading ? readingLayout : undefined)
      }
    />
  ) : null;

  const wrap = (body: ReactNode) =>
    reading ? (
      <div ref={readingRef} className="reading-sheet" style={readingStyle}>
        <ReadingChartProbe chart={chart} probeRef={probeRef} style={readingStyle} />
        {body}
      </div>
    ) : (
      body
    );

  if (!active)
    return wrap(
      <>
        {header}
        {render(undefined, viewportCells, undefined, reading ? readingLayout : undefined)}
        {printSheet}
      </>,
    );

  const pageWidthPx = Math.floor(plan.pageWidth);
  return wrap(
    <>
      <div ref={containerRef} className="sheet-pages">
        <SheetPageCards
          gridRef={gridRef}
          columns={plan.columns}
          pageWidth={pageWidthPx}
          minHeight={Math.round(m.availHeight)}
          windows={plan.windows}
          render={(win, i) => (
            <>
              {/* The masthead prints ON page 1, like every engraved score, and
                  nowhere else — so pages 2+ open with music at the top. */}
              {i === 0 && header && <div className="sheet-page-header">{header}</div>}
              {render(win, cells, slotChords, reading ? readingLayout : undefined)}
            </>
          )}
        />
      </div>
      {printSheet}
    </>,
  );
}
